import React from "react";
import Seo from "./Seo";
import { Section, Eyebrow, PageHero } from "./primitives";
import { Reveal, Stagger, StaggerItem } from "./Reveal";
import CTABanner from "./CTABanner";
import * as Icons from "lucide-react";

export default function FeatureDetailLayout({
  seoPath,
  seoTitle,
  eyebrow,
  title,
  highlight,
  description,
  image,
  imageAlt,
  eagle = false,
  noindex = false,
  benefits = [],
  howItWorks = [],
}) {
  return (
    <div>
      <Seo title={seoTitle} path={seoPath} description={description} noindex={noindex} />
      <PageHero eyebrow={eyebrow} title={title} highlight={highlight} description={description} eagle={eagle} />

      {image && (
        <Section className="-mt-4">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-white/10 gold-glow">
              <img src={image} alt={imageAlt} className="h-[280px] w-full object-cover md:h-[440px]" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-transparent" />
            </div>
          </Reveal>
        </Section>
      )}

      {benefits.length > 0 && (
        <Section className="mt-24">
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow>Capabilities</Eyebrow>
              <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-white md:text-4xl">
                What makes it powerful
              </h2>
            </Reveal>
          </div>
          <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b) => {
              const Icon = Icons[b.icon] || Icons.Sparkles;
              return (
                <StaggerItem key={b.title}>
                  <div className="h-full rounded-2xl border border-white/10 bg-[#0a0a0a] p-7 transition-colors duration-300 hover:border-gold/30">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-gold">
                      <Icon className="h-5 w-5" strokeWidth={1.6} />
                    </div>
                    <h3 className="mt-5 font-heading text-lg text-white">{b.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-zinc-400">{b.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </Section>
      )}

      {howItWorks.length > 0 && (
        <Section className="mt-28">
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow>How it works</Eyebrow>
              <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-white md:text-4xl">
                Designed to feel effortless
              </h2>
            </Reveal>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {howItWorks.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-white/10 bg-[#0a0a0a] p-7">
                  <span className="font-heading text-3xl font-semibold text-gold-gradient">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 font-heading text-lg text-white">{step.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-zinc-400">{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      )}

      <CTABanner />
    </div>
  );
}
